/**
 * Widget Loader
 *
 * Auto-initializes the widget using data attributes on the script tag.
 *
 * Usage (publisher's page):
 * <script
 *   src="https://widget.galacticmarkets.net/widget.js"
 *   data-api-key="pk_xxx"
 *   data-tenant-id="your-tenant-id"
 *   data-debug="true"
 *   async
 * ></script>
 *
 * Supported data attributes:
 * - data-api-key (required): API key for authentication
 * - data-tenant-id (required): Tenant identifier for tenant isolation
 * - data-debug (optional): Enable debug logging ("true" to enable). Also
 *   surfaces the one-shot `[pw] Predictor Widget v<version> (<env>)` banner
 *   on init; the banner is suppressed without this flag.
 * - data-position (optional): Where the widget mounts — case-insensitive.
 *   • "bottom-left" / "bottom-center" / "bottom-right" (default
 *     "bottom-right") — fixed-position desktop anchors. Mobile (< 768px)
 *     collapses all three to bottom-centre.
 *   • "inline" — mounts in normal document flow at the script tag's DOM
 *     location, adapts to parent width, and stays inline at every viewport
 *     (no mobile bottom-centre snap).
 * - data-default-view (optional): Which surface the widget opens in —
 *   case-insensitive. One of "pill" / "preview" / "card" (default "preview");
 *   missing, empty, or unrecognised values fall back to "preview". The user
 *   can still transition between surfaces afterward via the widget UI.
 * - data-allow-content-ingestion (optional): Opt in to page-content scraping
 *   for recommendation matching ("true" to enable). Default off — when absent
 *   or any value other than the exact string "true", the widget uses cached
 *   recommendations and the featured-markets fallback only. See README and
 *   INTEGRATION_GUIDE for the privacy posture.
 *
 * Once mounted, the container element exposes `data-version` and `data-env`
 * attributes (see `initWidget` below) so partners and support tooling can
 * sniff the running version programmatically regardless of the debug flag.
 */

import { createElement, render } from "preact";
import { WidgetApp } from "./components/widget-app";
import {
  debugLog,
  errorLog,
  getConfig,
  initConfig,
  setConfigPosition,
  warnLog,
  widgetEnv,
  widgetVersion,
  type WidgetConfig,
} from "./config";
import { currentLocale, initI18n } from "./i18n";
import styles from "./styles/compiled.css";
import { loadFonts } from "./styles/fonts";
import {
  defaultWidgetPosition,
  widgetContainerId,
  WidgetPosition,
  widgetSurfaceMinHeight,
} from "./utils/constants";
import { parseDefaultView } from "./utils/parse-default-view";
import { parsePosition } from "./utils/parse-position";

/** Track initialization state */
let isInitialized = false;

/**
 * Configuration extracted from script data attributes.
 */
interface ScriptConfig {
  apiKey: string;
  tenantId: string;
  locale?: string;
  config: Partial<WidgetConfig>;
  /**
   * Reference to the script element the widget loaded from. Captured at
   * module-execution time because `document.currentScript` is only valid
   * inside the synchronous script execution — by the time `initWidget`
   * runs from a DOMContentLoaded defer, currentScript is already `null`.
   * Used by the inline mount path to anchor the container at the script's
   * DOM location.
   */
  scriptElement: HTMLScriptElement;
}

/**
 * Extract configuration from the current script's data attributes.
 * Returns null if script element cannot be found or required attributes are missing.
 */
function getScriptConfig(): ScriptConfig | null {
  const script = document.currentScript as HTMLScriptElement | null;

  if (!script) {
    warnLog(
      "Could not find script element. " +
        "Ensure the widget script is loaded via a <script> tag, not dynamically inserted.",
    );
    return null;
  }

  const apiKey = script.dataset.apiKey;

  if (!apiKey) {
    errorLog(
      'Missing required "data-api-key" attribute. ' +
        'Usage: <script src="widget.js" data-api-key="pk_xxx" data-tenant-id="your-tenant-id"></script>',
    );
    return null;
  }

  const tenantId = script.dataset.tenantId;

  if (!tenantId) {
    errorLog(
      'Missing required "data-tenant-id" attribute. ' +
        'Usage: <script src="widget.js" data-api-key="pk_xxx" data-tenant-id="your-tenant-id"></script>',
    );
    return null;
  }

  return {
    apiKey,
    tenantId,
    locale: script.dataset.locale,
    config: {
      debug: script.dataset.debug === "true",
      position: parsePosition(script.dataset.position),
      defaultView: parseDefaultView(script.dataset.defaultView),
      allowContentIngestion: script.dataset.allowContentIngestion === "true",
    },
    scriptElement: script,
  };
}

/**
 * Initialize the widget with the given API key and optional config.
 * Creates Shadow DOM container and mounts Preact app.
 */
function initWidget(
  apiKey: string,
  tenantId: string,
  locale: string | undefined,
  config: Partial<WidgetConfig> | undefined,
  scriptElement: HTMLScriptElement,
): void {
  // Initialize configuration first (affects logging)
  initConfig({ ...config, apiKey });
  // Locale is fixed at boot; later renders read the active messages()
  initI18n(locale);

  if (isInitialized) {
    warnLog("Widget already initialized. Ignoring duplicate init.");
    return;
  }

  if (!apiKey || typeof apiKey !== "string") {
    errorLog(
      'Missing or invalid API key. Add data-api-key="your-key" to the script tag.',
    );
    return;
  }

  // Defer until the host DOM is fully parsed so the inline anchor and content
  // extraction see the complete page, not a mid-parse snapshot. Subsumes the
  // old !document.body guard — body is null while readyState is "loading", so
  // the async-script-before-body case still defers here.
  if (document.readyState === "loading") {
    debugLog("Document still parsing; deferring init to DOMContentLoaded.");
    document.addEventListener(
      "DOMContentLoaded",
      () => initWidget(apiKey, tenantId, locale, config, scriptElement),
      { once: true },
    );
    return;
  }

  // Create container element
  const container = document.createElement("div");
  container.id = widgetContainerId;
  container.dataset.version = widgetVersion;
  container.dataset.env = widgetEnv;

  // Check if container already exists (prevents duplicate mounting)
  if (document.getElementById(widgetContainerId)) {
    warnLog("Widget container already exists. Ignoring duplicate init.");
    return;
  }

  // Banner is emitted only on the actual-mount path so the deferred re-entry
  // from DOMContentLoaded doesn't double-log.
  debugLog(`Predictor Widget v${widgetVersion} (${widgetEnv})`);

  // Resolve where we can actually mount before mounting. When inline can't
  // anchor, this downgrades to the floating default and re-syncs runtime
  // config so the surfaces render the floating layout, not a stranded
  // full-width inline surface.
  const position = resolveMountPosition(
    config?.position ?? defaultWidgetPosition,
    scriptElement,
  );
  if (position !== getConfig().position) {
    setConfigPosition(position);
  }

  mountContainer(container, scriptElement, position);

  // Attach Shadow DOM for style isolation
  let shadow: ShadowRoot;
  try {
    shadow = container.attachShadow({ mode: "closed" });
  } catch (error) {
    errorLog("Failed to attach Shadow DOM:", error);
    container.remove();
    return;
  }

  void loadFonts();

  // Inject styles into Shadow DOM
  const styleElement = document.createElement("style");
  styleElement.textContent = styles;
  shadow.appendChild(styleElement);

  // Verify styles were loaded (defensive check)
  if (!styles || styles.length < 100) {
    warnLog(
      `CSS may not be loaded correctly (${styles?.length ?? 0} chars). ` +
        "Ensure compiled.css exists and pnpm build:css was run.",
    );
  }

  // Create mount point for Preact
  const mountPoint = document.createElement("div");
  mountPoint.id = "pw-app";
  // The widget renders its own copy, so it declares its own language rather
  // than inheriting the partner page's — see `currentLocale`.
  mountPoint.lang = currentLocale();
  shadow.appendChild(mountPoint);

  // Mount Preact application
  render(
    createElement(WidgetApp, {
      apiKey,
      tenantId,
      pageUrl: window.location.href,
      // Release the inline space reservation once the load resolves, so a
      // failed/empty load (which renders nothing) collapses to zero height
      // instead of leaving a permanent blank gap in the partner article.
      onSettled: () => {
        container.style.minHeight = "";
      },
    }),
    mountPoint,
  );

  isInitialized = true;
  debugLog("Widget initialized.");
}

/**
 * Resolve the effective mount position. `inline` only works when the script
 * tag is still connected and lives in the page body; when it's detached or
 * sits in `<head>`, browsers can't anchor the widget where the partner
 * expects. In those cases we fall back to the floating default and return it
 * (rather than just body-appending an inline-configured widget) so runtime
 * config and the Preact surfaces render a normal floating widget instead of a
 * full-width inline surface stranded at the end of `<body>`.
 */
function resolveMountPosition(
  requested: WidgetPosition,
  scriptElement: HTMLScriptElement,
): WidgetPosition {
  if (requested !== WidgetPosition.Inline) return requested;

  if (!scriptElement.parentNode || !scriptElement.isConnected) {
    warnLog(
      "Script element detached from DOM before inline mount; " +
        "falling back to the floating default.",
    );
    return defaultWidgetPosition;
  }

  if (scriptElement.parentNode === document.head) {
    warnLog(
      "data-position=inline requires the script tag to live in the " +
        "page body where you want the widget to appear, not in <head>. " +
        "Falling back to the floating default.",
    );
    return defaultWidgetPosition;
  }

  return WidgetPosition.Inline;
}

/**
 * Insert the widget container into the partner DOM. Floating positions go
 * under `document.body` so the fixed-position chrome anchors to the viewport.
 * `inline` inserts the container as a sibling immediately after the script
 * tag — that's where the partner pasted us, that's where the widget belongs
 * in document flow. `resolveMountPosition` has already downgraded inline to
 * the floating fallback when the script can't anchor, so the inline branch
 * here runs only when anchoring is known-safe.
 *
 * Inline also receives a `min-height` reservation sized to the configured
 * default view (`data-default-view`) so the partner article doesn't visibly
 * jump while the async widget data resolves. The reservation is transient:
 * `WidgetApp` clears it via `onSettled` once the load resolves (see
 * `initWidget`), so a failed/empty load collapses to zero height instead of
 * leaving a permanent gap.
 */
function mountContainer(
  container: HTMLDivElement,
  scriptElement: HTMLScriptElement,
  position: WidgetPosition | undefined,
): void {
  if (position === WidgetPosition.Inline && scriptElement.parentNode) {
    container.style.minHeight = widgetSurfaceMinHeight[getConfig().defaultView];
    scriptElement.parentNode.insertBefore(container, scriptElement.nextSibling);
    return;
  }
  document.body.appendChild(container);
}

// Auto-initialize from script data attributes
const scriptConfig = getScriptConfig();
if (scriptConfig) {
  initWidget(
    scriptConfig.apiKey,
    scriptConfig.tenantId,
    scriptConfig.locale,
    scriptConfig.config,
    scriptConfig.scriptElement,
  );
}
